import { type ComputedRef, type Ref, ref, watch } from 'vue'
import { debugConsoleError } from '@/modules/chat-matrix-fast/utils/chatDebugLog'
import {
  ChatApiService,
  type ChatMemberResponse,
} from '../services/chatApiService'
import {
  fetchMatrixRoomMembers,
  isSyntheticMatrixChatId,
} from '../services/matrixChatClient'
import {
  getCachedMatrixPresence,
  trackMatrixPresence,
} from '../services/matrixPresenceService'

interface UseMentionMembersOptions {
  chatId: Ref<number | string | null | undefined> | ComputedRef<number | string | null | undefined>
  matrixRoomId: Ref<string | null | undefined> | ComputedRef<string | null | undefined>
}

function applyPresence(list: ChatMemberResponse[]): ChatMemberResponse[] {
  return list.map(member => {
    const mxid = member.user_uuid?.trim()
    if (!mxid?.startsWith('@')) {
      return member
    }

    const online = getCachedMatrixPresence(mxid)
    if (online === undefined || member.online === online) {
      return member
    }

    return { ...member, online }
  })
}

function schedulePresenceEnrichment(
  list: ChatMemberResponse[],
  setMembers: (next: ChatMemberResponse[]) => void,
) {
  const mxids = list
    .map(member => member.user_uuid?.trim())
    .filter((mxid): mxid is string => Boolean(mxid?.startsWith('@')))

  if (!mxids.length) {
    return
  }

  void trackMatrixPresence(mxids).then(() => {
    setMembers(applyPresence(list))
  })
}

/** Участники для @-пикера: lazy load при первом `@`, backend-first, без eager N+1. */
export function useMentionMembers(options: UseMentionMembersOptions) {
  const members = ref<ChatMemberResponse[]>([])
  const isLoading = ref(false)

  let loadedKey: string | null = null
  let inFlight: Promise<void> | null = null

  function cacheKey(): string | null {
    const chatId = options.chatId.value
    const roomId = options.matrixRoomId.value?.trim()
    if (typeof chatId === 'number' && !isSyntheticMatrixChatId(chatId)) {
      return `chat:${chatId}`
    }
    if (roomId) {
      return `room:${roomId}`
    }
    return null
  }

  function reset() {
    members.value = []
    loadedKey = null
    inFlight = null
    isLoading.value = false
  }

  watch(
    () => cacheKey(),
    (next, prev) => {
      if (next !== prev) {
        reset()
      }
    },
  )

  async function ensureMentionMembers(): Promise<void> {
    const key = cacheKey()
    if (!key) {
      members.value = []
      return
    }

    if (loadedKey === key) {
      return
    }

    if (inFlight) {
      await inFlight
      if (loadedKey === key) {
        return
      }
    }

    isLoading.value = true
    inFlight = (async () => {
      try {
        const chatId = options.chatId.value
        const roomId = options.matrixRoomId.value?.trim()

        let loaded: ChatMemberResponse[] = []

        if (typeof chatId === 'number' && !isSyntheticMatrixChatId(chatId)) {
          try {
            const page = await ChatApiService.fetchMembers(chatId, { pageSize: 100 })
            loaded = page.members
            let cursor = page.nextCursor
            while (cursor) {
              const next = await ChatApiService.fetchMembers(chatId, {
                cursor,
                pageSize: 100,
              })
              loaded = [...loaded, ...next.members]
              cursor = next.nextCursor
            }
          } catch {
            loaded = []
          }
        }

        if (!loaded.length && roomId) {
          loaded = await fetchMatrixRoomMembers(roomId, { mode: 'light' })
        }

        members.value = loaded
        loadedKey = key
        schedulePresenceEnrichment(loaded, next => {
          if (loadedKey === key) {
            members.value = next
          }
        })
      } catch (error) {
        debugConsoleError('[useMentionMembers] load failed', error)
        members.value = []
        loadedKey = null
      } finally {
        isLoading.value = false
        inFlight = null
      }
    })()

    await inFlight
  }

  return {
    members,
    isLoading,
    ensureMentionMembers,
    reset,
  }
}
